import { Howl, Howler } from 'howler';
import { installIosUnlock } from './ios-unlock';

export interface AudioSample {
  key: string;
  src: string | string[];
  loop?: boolean;
  volume?: number;
}

export interface PlayOptions {
  volume?: number;
  rate?: number;
}

export interface Audio {
  load(samples: AudioSample[]): Promise<void>;
  play(key: string, opts?: PlayOptions): number;
  stop(idOrKey?: number | string): void;
  setMasterVolume(v: number): void;
  setMuted(muted: boolean): void;
  isUnlocked(): boolean;
  destroy(): void;
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}

export function createAudio(): Audio {
  const samples = new Map<string, Howl>();

  // Best-effort iOS unlock.
  const disposeUnlock = installIosUnlock(
    () => (Howler.ctx?.state as string | undefined) ?? 'running',
    () => void Howler.ctx?.resume(),
  );

  return {
    async load(specs) {
      await Promise.all(
        specs.map(
          (spec) =>
            new Promise<void>((resolve) => {
              const howl = new Howl({
                src: Array.isArray(spec.src) ? spec.src : [spec.src],
                loop: spec.loop ?? false,
                volume: spec.volume ?? 1,
                onload: () => resolve(),
                onloaderror: (_id, err) => {
                  console.warn('[audio] load failed', spec.key, err);
                  resolve();
                },
              });
              samples.set(spec.key, howl);
            }),
        ),
      );
    },

    play(key, opts) {
      const howl = samples.get(key);
      if (!howl) {
        console.warn(`[audio] unknown sample: ${key}`);
        return -1;
      }
      const id = howl.play() as number;
      if (opts?.volume !== undefined) howl.volume(clamp01(opts.volume), id);
      if (opts?.rate !== undefined) howl.rate(opts.rate, id);
      return id;
    },

    stop(idOrKey) {
      if (idOrKey === undefined) {
        for (const h of samples.values()) h.stop();
        return;
      }
      if (typeof idOrKey === 'string') {
        samples.get(idOrKey)?.stop();
      } else {
        for (const h of samples.values()) h.stop(idOrKey);
      }
    },

    setMasterVolume(v) {
      Howler.volume(clamp01(v));
    },

    setMuted(muted) {
      Howler.mute(muted);
    },

    isUnlocked() {
      return (Howler.ctx?.state as string | undefined) === 'running';
    },

    destroy() {
      disposeUnlock();
      for (const h of samples.values()) h.unload();
      samples.clear();
    },
  };
}
